import { chipDenominations } from '@tira/design-tokens';
import type { CSSProperties, JSX } from 'react';
import './Chip.css';

/**
 * Chips carry the "T" monogram and a bare integer. There is no currency
 * symbol here and there must never be one — design spec §2, PRD §7.3.
 */

export type Denomination = (typeof chipDenominations)[number]['value'];

function faceFor(value: Denomination): (typeof chipDenominations)[number] {
  // Denomination is closed, so the fallback only guards a bad cast at a boundary.
  return chipDenominations.find((d) => d.value === value) ?? chipDenominations[0];
}

export interface ChipProps {
  value: Denomination;
  /** Diameter in px. The component sheet's reference chip is 94. */
  size?: number;
  className?: string;
}

export function Chip({ value, size = 94, className }: ChipProps): JSX.Element {
  const face = faceFor(value);
  const style = {
    '--chip-size': `${size}px`,
    '--chip-ring': face.ring,
    '--chip-inner': face.inner,
    '--chip-ink': face.text,
  } as CSSProperties;

  return (
    <div
      className={className === undefined ? 'chip' : `chip ${className}`}
      style={style}
      aria-hidden="true"
    >
      <span className="chip__face">
        <span className="chip__monogram">T</span>
        <span className="chip__value">{face.value}</span>
      </span>
    </div>
  );
}
